import type { SearchParams } from '@/server/searchParams.ts';
import { URLParameters } from '@/app/common/urlParameters.ts';
import type { TranslationFunction } from '@/app/components/translations/Translation';
import { type ControlProps, type Option, ControlType, FieldType } from '../../../Form';
import {
  type FormOption,
  getMultilistboxValue,
  OPTIONS_SORT,
  parseAddLabel,
  sortOptionsByAlphabetic,
  sortOptionsByNum,
} from '../utils';
import {
  activityFacilities,
  familyFacilities,
  foodFacilities,
  generalFacilities,
  wellnessFacilities,
} from './getFacilitiesField/utils';

type GetListboxContainer = {
  fieldName: string;
  containerTitle: string;
  containerName: string;
  options: FormOption[];
  value: (number | string)[];
  clearAll: string;
  fieldClassName: string;
  hide?: boolean;
  isMobile?: boolean;
  isSidebar?: boolean;
  isHorizontal?: boolean;
  hasTwoColumns?: boolean;
  optionsSort?: OPTIONS_SORT;
  limit?: number;
  searchParams: SearchParams;
  t: TranslationFunction;
};

const getLimit = (hasTwoColumns: boolean, controlsCount: number): number | undefined => {
  if (hasTwoColumns) {
    const limit = 10;
    const isLimitApplicable = limit * 2 < controlsCount;
    return isLimitApplicable ? limit : undefined;
  } else {
    const limit = 15;
    const isLimitApplicable = limit < controlsCount;
    return isLimitApplicable ? limit : undefined;
  }
};

const sortOptions = (optionsSort: OPTIONS_SORT, optionsArray: Option[]): Option[] => {
  if (optionsSort === OPTIONS_SORT.NUM) return sortOptionsByNum(sortOptionsByAlphabetic(optionsArray));
  if (optionsSort === OPTIONS_SORT.ALPHABET) return sortOptionsByAlphabetic(optionsArray);
  return optionsArray;
};

const getFacilitiesContainer = ({
  fieldName,
  containerName,
  containerTitle,
  options,
  value,
  clearAll,
  fieldClassName,
  hide,
  isMobile,
  isSidebar,
  isHorizontal,
  hasTwoColumns = false,
  optionsSort = OPTIONS_SORT.NUM,
  limit,
  searchParams,
  t,
}: GetListboxContainer): ControlProps => {
  const parseOptions = (opts: Option[]): Option[] =>
    opts.map((option) => ({
      ...option,
      ...(option.addLabel
        ? {
            addLabel: parseAddLabel({
              withBrackets: !isMobile && !isSidebar,
              count: option.addLabel,
            }),
          }
        : {}),
    }));

  const parsedOptions = parseOptions(options);
  const generalOptions = parsedOptions.filter((x) => generalFacilities.includes(x.value));
  const familyOptions = parsedOptions.filter((x) => familyFacilities.includes(x.value));
  const activityOptions = parsedOptions.filter((x) => activityFacilities.includes(x.value));
  const wellnessOptions = parsedOptions.filter((x) => wellnessFacilities.includes(x.value));
  const foodOptions = parsedOptions.filter((x) => foodFacilities.includes(x.value));
  const isFamilySearch = !!searchParams?.[URLParameters.AGES];

  const baseControl = {
    control: ControlType.LISTBOX,
    type: FieldType.MULTILISTBOX,
    name: fieldName,
    className: fieldClassName,
    options: sortOptions(optionsSort, parsedOptions),
    value,
    default: [],
    expanded: true,
    minEl: 2,
    ...((!isMobile && !isSidebar && !isHorizontal) || hasTwoColumns ? { columns: 2 } : { columns: 1 }),
    ...(isMobile || isSidebar ? { iconOnTheRight: true } : {}),
  };

  const getNewAccommodationOptions = (name: string, label: string | null, option: Option[]) => {
    const controlValue = getMultilistboxValue(searchParams[name], true);
    return [
      {
        ...baseControl,
        name,
        ...(label ? { label: { text: label } } : undefined),
        options: sortOptions(optionsSort, option),
        value: controlValue,
        limit,
      },
    ];
  };

  const hasOtherOptions =
    familyOptions.length > 0 || activityOptions.length > 0 || wellnessOptions.length > 0 || foodOptions.length > 0;
  const newOptions = () => [
    ...(generalOptions.length > 0
      ? getNewAccommodationOptions(
          URLParameters.GENERAL_ACCOMODATION_FACILITIES,
          hasOtherOptions ? t('vacation.oss.filters.general.title') : null,
          generalOptions
        )
      : []),
    ...(familyOptions.length > 0 && isFamilySearch
      ? getNewAccommodationOptions(
          URLParameters.FAMILY_ACCOMODATION_FACILITIES,
          t('vacation.oss.filters.family.title'),
          familyOptions
        )
      : []),
    ...(activityOptions.length > 0
      ? getNewAccommodationOptions(
          URLParameters.ACTIVITY_ACCOMODATION_FACILITIES,
          t('vacation.oss.filters.activities.title'),
          activityOptions
        )
      : []),
    ...(wellnessOptions.length > 0
      ? getNewAccommodationOptions(
          URLParameters.WELLNESS_ACCOMODATION_FACILITIES,
          t('vacation.oss.filters.wellness.title'),
          wellnessOptions
        )
      : []),
    ...(foodOptions.length > 0
      ? getNewAccommodationOptions(
          URLParameters.FOOD_ACCOMODATION_FACILITIES,
          t('vacation.oss.filters.food.title'),
          foodOptions
        )
      : []),
  ];

  return {
    control: ControlType.CONTAINER,
    name: containerName,
    top: { text: containerTitle },
    limit: getLimit(hasTwoColumns, options.length),
    controls: [...newOptions()],
    controlProps: {
      clearAll,
      showMoreLabel: t('vacation.oss.cards.view_more'),
    },
    hide,
    columns: hasTwoColumns ? 2 : 1,
  };
};

export default getFacilitiesContainer;
